/**
 * Credential Dialog State Machine Hook
 *
 * Manages the multi-phase credential dialog flow:
 * form → saving → testing → success/failure
 */

import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isEmailInTestSignInDomain } from '@chatsdet/types';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import { useBrowserStream } from '../../../hooks/useBrowserStream';
import type { CredentialFormData } from '../CredentialForm';
import { useProvisionEmail } from './useProvisionEmail';
import { useTestSignInQuery } from '../settings/test-sign-in/useTestSignIn';

export type DialogPhase =
  | 'form' // Editing credential form
  | 'saving' // API call in progress
  | 'testing' // Login test running with browser stream
  | 'success' // Login succeeded
  | 'failure'; // Login failed - show retry options

type UseCredentialDialogStateOptions = {
  propertyId: string;
  initialCredentialId?: string;
  /** Pre-binds the new credential to a persona (credential template). */
  initialTemplateId?: string | null;
  onSuccess?: (credentialId: string) => void;
  onSkip?: () => void;
  onClose?: () => void;
};

type CredentialMode =
  | 'choosing'
  | 'auto'
  | 'manual'
  | 'api_key'
  | 'api_login'
  | 'api_basic'
  | 'google_oauth'
  | 'test_sign_in';

type UseCredentialDialogStateReturn = {
  phase: DialogPhase;
  credentialMode: CredentialMode;
  formData: CredentialFormData;
  setFormData: React.Dispatch<React.SetStateAction<CredentialFormData>>;
  credentialId: string | null;
  streamId: string | null;
  browserStream: ReturnType<typeof useBrowserStream>;
  isLoading: boolean;
  isProvisioning: boolean;
  errorMessage: string | null;

  // Actions
  handleSave: () => void;
  handleSaveOnly: () => void;
  handleRetry: () => void;
  handleSkip: () => void;
  handleCancel: () => void;
  handleCancelTest: () => void;
  handleContinue: () => void;
  provisionEmailUser: () => void;
  chooseManualEntry: () => void;
  chooseApiKey: () => void;
  chooseApiBasic: () => void;
  chooseApiLogin: () => void;
  chooseGoogleOauth: () => void;
  chooseTestSignIn: () => void;

  // For form validation
  isFormValid: boolean;
  isProvisioned: boolean;
};

const emptyFormData: CredentialFormData = {
  name: '',
  authProvider: 'basic',
  data: {},
};

export function useCredentialDialogState({
  propertyId,
  initialCredentialId,
  initialTemplateId,
  onSuccess,
  onSkip,
  onClose,
}: UseCredentialDialogStateOptions): UseCredentialDialogStateReturn {
  const qc = useQueryClient();

  // Base form state carries the persona pre-binding (create-from-persona) so
  // resets (cancel, auto-provision) keep the credential inside the persona.
  const baseFormData = React.useMemo<CredentialFormData>(
    () => ({ ...emptyFormData, templateId: initialTemplateId ?? null }),
    [initialTemplateId]
  );

  const [phase, setPhase] = React.useState<DialogPhase>('form');
  const [formData, setFormData] = React.useState<CredentialFormData>(baseFormData);
  const [credentialId, setCredentialId] = React.useState<string | null>(
    initialCredentialId ?? null
  );
  const [streamId, setStreamId] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [isProvisioned, setIsProvisioned] = React.useState(false);
  const {
    provision: provisionEmail,
    isProvisioning,
    error: provisionError,
  } = useProvisionEmail();
  const [credentialMode, setCredentialMode] = React.useState<CredentialMode>(
    initialCredentialId ? 'manual' : 'choosing'
  );
  // Only needed to validate a test_sign_in credential's email against the
  // org's provider domain; gated so orgs without the test-sign-in provider
  // (or a dialog that isn't on that auth mode) never fire the request.
  const isTestSignIn = formData.authProvider === 'test_sign_in';
  const testSignInQuery = useTestSignInQuery({ enabled: isTestSignIn });

  // Track if we're in edit mode
  const isEditMode = !!initialCredentialId;

  // Browser stream for live test
  const browserStream = useBrowserStream(streamId, { autoConnect: true });

  // Load initial credential data for edit mode
  React.useEffect(() => {
    if (initialCredentialId && propertyId) {
      setCredentialId(initialCredentialId);
      fetch(`${API_URL}/org/properties/${propertyId}/credentials/${initialCredentialId}`, {
        credentials: 'include',
      })
        .then((r) => {
          if (!r.ok) throw new Error('Failed to fetch credential');
          return r.json();
        })
        .then((data) => {
          const cred = data.credential;
          setFormData({
            name: cred.name,
            authProvider: cred.authProvider,
            data: cred.data,
            metadata: cred.metadata,
            tags: cred.tags,
            notes: cred.notes,
            loginInstructions: cred.loginInstructions,
            sessionVerificationGuidance: cred.sessionVerificationGuidance,
            environmentId: cred.environmentId ?? null,
            templateId: cred.templateId ?? null,
            isDefault: cred.isDefault,
            cloudflareAccessIntegrationId: cred.cloudflareAccessIntegrationId || null,
            provisioningInstructions: cred.provisioningInstructions || undefined,
            isProvisioned: cred.isProvisioned,
            // Sensitive values arrive masked; round-trip unchanged unless the user edits them.
            variableValues: cred.variableValues ?? undefined,
          });
          if (cred.isProvisioned === false) {
            setIsProvisioned(false);
          } else if (cred.isProvisioned === true) {
            setIsProvisioned(true);
          }
        })
        .catch((error) => {
          console.error('Failed to fetch credential:', error);
          setErrorMessage('Failed to load credential details');
        });
    } else {
      setCredentialId(null);
    }
  }, [initialCredentialId, propertyId]);

  // Create credential mutation
  const createCredential = useMutation({
    mutationFn: async () => {
      const payload = {
        ...formData,
        sessionVerificationGuidance: formData.sessionVerificationGuidance?.trim() || null,
      };
      const r = await fetch(`${API_URL}/org/properties/${propertyId}/credentials`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to save credential');
      return r.json();
    },
  });

  // Update credential mutation
  const updateCredential = useMutation({
    mutationFn: async (credId: string) => {
      const payload = {
        ...formData,
        sessionVerificationGuidance: formData.sessionVerificationGuidance?.trim() || null,
      };
      const r = await fetch(`${API_URL}/org/properties/${propertyId}/credentials/${credId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to save credential');
      return r.json();
    },
  });

  // Start login test mutation
  const startLoginTest = useMutation({
    mutationFn: async (credId: string) => {
      const r = await fetch(
        `${API_URL}/org/properties/${propertyId}/credentials/${credId}/mcp-test/browser-stream`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
        }
      );
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
  });

  // Start API login test mutation
  const startApiLoginTest = useMutation({
    mutationFn: async (credId: string) => {
      const r = await fetch(
        `${API_URL}/org/properties/${propertyId}/credentials/${credId}/api-login-test/stream`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
        }
      );
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
  });

  // Test completion is driven by the orchestrator's `browser.test-end` signal,
  // not by `browser.status` 'disconnected'/'error' — the latter can fire
  // mid-run as the underlying browser session cycles (e.g., during a session
  // swap inside the fresh-login flow), which previously caused a false
  // "Login Failed" before the test actually finished.
  const testEnd = browserStream.state.testEnd;
  React.useEffect(() => {
    if (phase !== 'testing' || !testEnd) return;

    if (testEnd.outcome === 'success') {
      setPhase('success');
    } else {
      setPhase('failure');
      setErrorMessage(testEnd.message ?? 'Login could not be completed');
    }
  }, [testEnd, phase]);

  // Handle save action
  const handleSave = React.useCallback(async () => {
    setPhase('saving');
    setErrorMessage(null);

    try {
      let savedCredentialId: string;

      if (credentialId) {
        await updateCredential.mutateAsync(credentialId);
        savedCredentialId = credentialId;
      } else {
        const result = await createCredential.mutateAsync();
        savedCredentialId = result.credential.id;
        setCredentialId(savedCredentialId);
      }

      // Start the appropriate test based on auth provider
      if (formData.authProvider === 'api_login') {
        const testResult = await startApiLoginTest.mutateAsync(savedCredentialId);
        setStreamId(testResult.streamId);
        setPhase('testing');
      } else {
        const testResult = await startLoginTest.mutateAsync(savedCredentialId);
        setStreamId(testResult.streamId);
        setPhase('testing');
      }

      // Invalidate credentials query
      qc.invalidateQueries({ queryKey: ['property-creds', propertyId] });
      qc.invalidateQueries({ queryKey: ['org-credentials-with-properties'] });
    } catch (error) {
      setPhase('form');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to save credential');
    }
  }, [
    isEditMode,
    credentialId,
    formData,
    propertyId,
    createCredential,
    updateCredential,
    startLoginTest,
    startApiLoginTest,
    qc,
  ]);

  // Handle save without testing
  const handleSaveOnly = React.useCallback(async () => {
    setPhase('saving');
    setErrorMessage(null);

    try {
      let savedCredentialId: string;

      if (credentialId) {
        await updateCredential.mutateAsync(credentialId);
        savedCredentialId = credentialId;
      } else {
        const result = await createCredential.mutateAsync();
        savedCredentialId = result.credential.id;
        setCredentialId(savedCredentialId);
      }

      qc.invalidateQueries({ queryKey: ['property-creds', propertyId] });
      qc.invalidateQueries({ queryKey: ['org-credentials-with-properties'] });
      onSuccess?.(savedCredentialId);
      onClose?.();
    } catch (error) {
      setPhase('form');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to save credential');
    }
  }, [
    credentialId,
    formData,
    propertyId,
    createCredential,
    updateCredential,
    qc,
    onSuccess,
    onClose,
  ]);

  // Handle retry action - go back to form with data preserved
  const handleRetry = React.useCallback(() => {
    setPhase('form');
    setStreamId(null);
    setErrorMessage(null);
    browserStream.reset();
  }, [browserStream]);

  // Handle skip action - close without completing test
  const handleSkip = React.useCallback(() => {
    browserStream.disconnect();
    qc.invalidateQueries({ queryKey: ['property-creds', propertyId] });
    qc.invalidateQueries({ queryKey: ['org-credentials-with-properties'] });
    // Refresh test-sign-in activity too, so a completed Save & Test's Recent
    // sign-ins panel (and the provider/clients query) reflect it.
    qc.invalidateQueries({ queryKey: ['test-sign-in'] });
    onSkip?.();
    onClose?.();
  }, [browserStream, qc, propertyId, onSkip, onClose]);

  // Handle cancel action
  const handleCancel = React.useCallback(() => {
    browserStream.disconnect();
    setFormData(baseFormData);
    setPhase('form');
    setStreamId(null);
    setCredentialId(initialCredentialId ?? null);
    setErrorMessage(null);
    setIsProvisioned(false);
    setCredentialMode(initialCredentialId ? 'manual' : 'choosing');
    onClose?.();
  }, [browserStream, baseFormData, initialCredentialId, onClose]);

  // Handle cancel test action
  const handleCancelTest = React.useCallback(() => {
    browserStream.disconnect();
    // Go back to form if we're in testing phase
    setPhase('form');
    setStreamId(null);
    setErrorMessage(null);
  }, [browserStream]);

  // Handle continue action (after success)
  const handleContinue = React.useCallback(() => {
    if (credentialId) {
      onSuccess?.(credentialId);
    }
    qc.invalidateQueries({ queryKey: ['property-creds', propertyId] });
    qc.invalidateQueries({ queryKey: ['org-credentials-with-properties'] });
    // Refresh test-sign-in activity too, so a completed Save & Test's Recent
    // sign-ins panel (and the provider/clients query) reflect it.
    qc.invalidateQueries({ queryKey: ['test-sign-in'] });
    onClose?.();
  }, [credentialId, onSuccess, qc, propertyId, onClose]);

  // Provision email user: generate fun name, check uniqueness, pre-fill form
  const provisionEmailUser = React.useCallback(async () => {
    setCredentialMode('auto');
    setErrorMessage(null);

    const result = await provisionEmail();
    if (!result) {
      if (provisionError) setErrorMessage(provisionError);
      return;
    }

    setFormData({
      ...baseFormData,
      name: result.funName,
      data: { username: result.email, password: result.passphrase },
      createInboxRecipient: true,
    });
    setIsProvisioned(true);
  }, [provisionEmail, provisionError, baseFormData]);

  // Choose manual credential entry
  const chooseManualEntry = React.useCallback(() => {
    setCredentialMode('manual');
  }, []);

  // Choose API key credential entry
  const chooseApiKey = React.useCallback(() => {
    setCredentialMode('api_key');
    setFormData((prev) => ({
      ...prev,
      authProvider: 'api_key' as const,
      data: {},
      metadata: {},
    }));
  }, []);

  // Choose API basic auth credential entry
  const chooseApiBasic = React.useCallback(() => {
    setCredentialMode('api_basic');
    setFormData((prev) => ({
      ...prev,
      authProvider: 'api_basic' as const,
      data: { username: '', password: '' },
    }));
  }, []);

  // Choose API login credential entry
  const chooseApiLogin = React.useCallback(() => {
    setCredentialMode('api_login');
    setFormData((prev) => ({
      ...prev,
      authProvider: 'api_login' as const,
      data: { loginFields: { username: '', password: '' } },
      metadata: {},
    }));
  }, []);

  // Choose Google minted-session (refresh-token) credential entry
  const chooseGoogleOauth = React.useCallback(() => {
    setCredentialMode('google_oauth');
    setFormData((prev) => ({
      ...prev,
      authProvider: 'google_oauth' as const,
      data: { clientId: '', clientSecret: '', refreshToken: '' },
      metadata: {},
    }));
  }, []);

  // Choose test sign-in — the credential IS the test user (email + name);
  // Cofactor signs it in deterministically through the org's OIDC provider.
  const chooseTestSignIn = React.useCallback(() => {
    setCredentialMode('test_sign_in');
    setFormData((prev) => ({
      ...prev,
      authProvider: 'test_sign_in' as const,
      data: { email: '', name: '' },
      metadata: {},
    }));
  }, []);

  // Form validation - varies by provider type
  const isFormValid = React.useMemo(() => {
    if (!formData.name) return false;
    if (formData.authProvider === 'api_key') return !!formData.data.key;
    // All three are required with no default: the app validates the minted
    // token against its own OAuth client, so there is nothing to fall back to.
    if (formData.authProvider === 'google_oauth')
      return (
        !!formData.data.clientId && !!formData.data.clientSecret && !!formData.data.refreshToken
      );
    if (formData.authProvider === 'api_basic')
      return !!formData.data.username && !!formData.data.password;
    if (formData.authProvider === 'test_sign_in') {
      const provider = testSignInQuery.data?.data.provider;
      if (!provider) return false;
      return (
        !!formData.data.email &&
        !!formData.data.name &&
        isEmailInTestSignInDomain(formData.data.email, provider.emailDomain)
      );
    }
    if (formData.authProvider === 'api_login') {
      const loginFields = formData.data.loginFields as Record<string, string> | undefined;
      const hasLoginFields = loginFields && Object.values(loginFields).some((v) => !!v);
      return !!formData.metadata?.tokenUrl && !!hasLoginFields;
    }
    return !!formData.data.username;
  }, [formData, testSignInQuery.data]);

  return {
    phase,
    credentialMode,
    formData,
    setFormData,
    credentialId,
    streamId,
    browserStream,
    isLoading:
      createCredential.isPending ||
      updateCredential.isPending ||
      startLoginTest.isPending ||
      startApiLoginTest.isPending,
    isProvisioning,
    errorMessage,
    handleSave,
    handleSaveOnly,
    handleRetry,
    handleSkip,
    handleCancel,
    handleCancelTest,
    handleContinue,
    provisionEmailUser,
    chooseManualEntry,
    chooseApiKey,
    chooseApiBasic,
    chooseApiLogin,
    chooseGoogleOauth,
    chooseTestSignIn,
    isFormValid,
    isProvisioned,
  };
}
